import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { runJob } from "@/lib/jobs";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Kpi, useSd } from "@/components/servicedesk/SdBits";

const Card = ({ title, children, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}><h3 className="text-sm font-semibold mb-3">{title}</h3>{children}</div>
);

const Bars = ({ data, color = "#0284c7" }) => (
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={data} layout="vertical" margin={{ left: 10, right: 10 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} /><XAxis type="number" allowDecimals={false} fontSize={11} />
      <YAxis type="category" dataKey="name" width={130} fontSize={11} /><Tooltip /><Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const pct = (v) => (v == null ? "—" : `${v}%`);

export default function SdDashboard() {
  const { me } = useSd();
  const [f, setF] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  useEffect(() => { if (me?.is_staff) api.get("/servicedesk/dashboard", { params: f }).then(({ data }) => setD(data)).catch(() => {}); }, [me, f]);
  if (me && !me.is_staff) return <Navigate to="/servicedesk/my" replace />;
  if (!d) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  const k = d.kpi;
  const exp = (kind) => runJob(() => api.get("/servicedesk/reports/export", { params: { ...f, kind, background: true } }).then((r) => r.data)).catch(() => {});
  return (
    <div data-testid="sd-dashboard">
      <PageHeader title="Service Desk Dashboard" subtitle="Live ticket performance across your companies"
        actions={<div className="flex flex-wrap gap-2">
          <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="w-36" data-testid="sd-dash-start" />
          <Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="w-36" data-testid="sd-dash-end" />
          <Button variant="outline" onClick={() => exp("tickets")} data-testid="sd-export-tickets"><Icons.Download className="h-4 w-4 mr-1" />Tickets &amp; SLA</Button>
          <Button variant="outline" onClick={() => exp("technicians")} data-testid="sd-export-techs"><Icons.Download className="h-4 w-4 mr-1" />Technicians</Button>
        </div>} />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-4">
        <Kpi label="Total tickets" value={k.total} icon="Ticket" testid="sd-kpi-total" />
        <Kpi label="Active" value={k.active} icon="Activity" tone="text-sky-700" testid="sd-kpi-active" />
        <Kpi label="Resolved" value={k.resolved} icon="CheckCircle2" tone="text-emerald-700" testid="sd-kpi-resolved" />
        <Kpi label="SLA breaches" value={k.breaches} icon="AlertTriangle" tone="text-red-600" testid="sd-kpi-breaches" />
        <Kpi label="Unassigned" value={k.unassigned} icon="UserX" tone="text-amber-600" testid="sd-kpi-unassigned" />
        <Kpi label="Major incidents open" value={k.major} icon="Siren" tone="text-red-700" testid="sd-kpi-major" />
        <Kpi label="First response compliance" value={pct(k.first_response_compliance)} icon="Timer" testid="sd-kpi-frc" />
        <Kpi label="Resolution compliance" value={pct(k.resolution_compliance)} icon="Gauge" testid="sd-kpi-rc" />
        <Kpi label="Avg resolution (business h)" value={k.avg_resolution_hours} icon="Hourglass" testid="sd-kpi-avg" />
        <Kpi label="Reopened" value={pct(k.reopened_pct)} icon="RotateCcw" testid="sd-kpi-reopened" />
        <Kpi label="Closed" value={k.closed} icon="Lock" testid="sd-kpi-closed" />
        <Kpi label="Satisfaction (1–5)" value={k.csat} icon="Star" tone="text-amber-600" testid="sd-kpi-csat" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Monthly trend" testid="sd-chart-trend">
          <ResponsiveContainer width="100%" height={220}><LineChart data={d.trend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend />
            <Line dataKey="created" stroke="#0284c7" strokeWidth={2} /><Line dataKey="resolved" stroke="#059669" strokeWidth={2} /></LineChart></ResponsiveContainer>
        </Card>
        <Card title="Aging backlog" testid="sd-chart-aging"><Bars data={d.aging} color="#f59e0b" /></Card>
        <Card title="By priority" testid="sd-chart-priority"><Bars data={d.by_priority} color="#dc2626" /></Card>
        <Card title="By category" testid="sd-chart-category"><Bars data={d.by_category.slice(0, 8)} /></Card>
        <Card title="By company" testid="sd-chart-company"><Bars data={d.by_company} color="#4f46e5" /></Card>
        <Card title="Technician workload" testid="sd-tech-table">
          <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="text-slate-500"><tr><th className="text-left py-1">Technician</th><th className="text-right">Assigned</th><th className="text-right">Open</th><th className="text-right">Resolved</th><th className="text-right">Breached</th></tr></thead>
            <tbody>{d.by_technician.map((x) => <tr key={x.name} className="border-t border-slate-100"><td className="py-1.5">{x.name}</td><td className="text-right">{x.assigned}</td><td className="text-right">{x.open}</td><td className="text-right">{x.resolved}</td><td className="text-right text-red-600">{x.breached}</td></tr>)}
              {!d.by_technician.length && <tr><td colSpan={5} className="py-4 text-center text-slate-400">No assignments yet</td></tr>}</tbody></table></div>
        </Card>
      </div>
    </div>
  );
}
